import React, { useEffect, useMemo, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Form,
  Input,
  Label,
  Row,
  Col,
  FormFeedback,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import {
  getReceiptsList,
  getReceiptFields,
  storeProviderOutFromReceipts,
} from "store/provider-in/provider-in-action"
import ReceiptsList from "../ProviderOut/ReceiptsList"
import SAlert from "components/Alert"

const ReceiptsListModal = ({ isOpen, toggle, receiptData, onSuccess }) => {
  const dispatch = useDispatch()
  const { receiptsList, receiptFields, loading } = useSelector(
    state => state.providerIn
  )
  const [selectedReceipts, setSelectedReceipts] = useState([])

  const totalSelectedReceiptsQuantity = useMemo(() => {
    return selectedReceipts.reduce(
      (acc, receipt) => acc + parseFloat(receipt.out_quantity || 0),
      0
    )
  }, [selectedReceipts])

  // Form validation schema
  const validationSchema = Yup.object({
    quantity: Yup.number()
      .min(1, "Quantity must be at least 1")
      .required("Quantity is required"),
  })

  // Formik form
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      quantity: receiptData?.first_in_qty_per_ip || 0,
    },
    validationSchema: validationSchema,
    onSubmit: values => {
      handleAcceptReceipt(values)
    },
  })

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && receiptData?.id) {
      dispatch(getReceiptsList(receiptData.id))
      dispatch(getReceiptFields(receiptData.id))
    }
  }, [isOpen, receiptData?.id, dispatch])

  // Handle accepting the receipt
  const handleAcceptReceipt = async formData => {
    if (receiptData?.id && selectedReceipts.length > 0) {
      try {
        const requestData = {
          receipt_id: receiptData.id,
          selected_receipts: selectedReceipts,
          quantity: receiptData?.first_in_qty_per_ip,
        }

        dispatch(storeProviderOutFromReceipts(requestData))
        onSuccess && onSuccess()
        toggle()
      } catch (error) {
        console.error("Error accepting receipt:", error)
      }
    } else {
      SAlert({
        title: "Error",
        text: "Please select at least one receipt",
        status: "error",
      })
    }
  }

  // Handle adding receipt to selected list
  const onAddReceipt = receiptData => {
    setSelectedReceipts(prev => [...prev, receiptData])
  }

  // Handle removing receipt from selected list
  const onRemoveReceipt = receiptId => {
    setSelectedReceipts(prev =>
      prev.filter(item => item.provider_in_id !== receiptId)
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        Accept Receipt - {receiptData?.item?.item_name || "Unknown Item"}
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* Receipt Details Form */}
          <Col md={12}>
            <Card>
              <CardBody>
                <h5 className="card-title">Receipt Details</h5>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <Label htmlFor="item_name">Item Name</Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="item_name"
                          name="item_name"
                          value={receiptFields?.data?.item?.item_name || ""}
                          disabled
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="batch_number">Batch Number</Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="batch_number"
                          name="batch_number"
                          value={receiptFields?.data?.batch_number || ""}
                          disabled
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="expire_date">Expire Date</Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="expire_date"
                          name="expire_date"
                          value={receiptFields?.data?.expire_date || ""}
                          disabled
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="quantity">Quantity</Label>
                        <Input
                          type="number"
                          className="form-control"
                          id="quantity"
                          name="quantity"
                          disabled={true}
                          value={formik.values.quantity}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.quantity && formik.errors.quantity
                          }
                        />
                        {formik.touched.quantity && formik.errors.quantity && (
                          <FormFeedback type="invalid">
                            {formik.errors.quantity}
                          </FormFeedback>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>

          {/* Available Receipts List */}
          <Col md={12}>
            <Card>
              <CardBody>
                <h5 className="card-title">Available Receipts</h5>
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <ReceiptsList
                    data={receiptsList?.data || []}
                    onSubmitAddHandler={onAddReceipt}
                    submited={selectedReceipts}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Selected Receipts Summary */}
        {selectedReceipts.length > 0 && (
          <Row className="mt-3">
            <Col md={12}>
              <Card>
                <CardBody>
                  <h5 className="card-title">
                    Selected Receipts ({selectedReceipts.length})
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Batch</th>
                          <th>Quantity</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReceipts.map((receipt, index) => (
                          <tr key={index}>
                            <td>{receipt.batch_number}</td>
                            <td>{receipt.batch_number}</td>
                            <td>{receipt.out_quantity}</td>
                            <td>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() =>
                                  onRemoveReceipt(receipt.provider_in_id)
                                }
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={formik.handleSubmit}
          disabled={
            loading ||
            !formik.isValid ||
            selectedReceipts.length === 0 ||
            totalSelectedReceiptsQuantity !== receiptData?.first_in_qty_per_ip
          }
        >
          {loading ? "Processing..." : "Accept Receipt"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ReceiptsListModal
